/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import type { RegenerationStatus } from '../../hooks/useRegenerationStatus';

type StatusBannerProps = {
	status: RegenerationStatus;
};

const styles = cssMap({
	banner: {
		boxSizing: 'border-box',
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		font: token('font.body.small'),
		backgroundColor: token('elevation.surface.sunken'),
		color: token('color.text.subtle'),
	},
	bannerSuccess: {
		backgroundColor: token('color.background.success'),
		color: token('color.text.success'),
	},
});

type BannerContent = {
	icon: React.ReactNode;
	label: typeof aiSuggestionsMessages.cardStaleBannerLabel;
};

function getBannerContent(status: RegenerationStatus): BannerContent {
	return {
		regenerating: {
			icon: <Spinner size="xsmall" />,
			label: aiSuggestionsMessages.cardRegeneratingBannerLabel,
		},
		updated: {
			icon: <CheckCircleIcon label="" size="small" color={token('color.icon.success')} />,
			label: aiSuggestionsMessages.cardUpdatedBannerLabel,
		},
		stale: {
			icon: <HistoryIcon label="" size="small" color={token('color.icon.subtle')} />,
			label: aiSuggestionsMessages.cardStaleBannerLabel,
		},
	}[status];
}

/**
 * Status banner shown at the top of a suggestion card body — while the
 * suggestion is being regenerated, once it's confirmed updated, or when it's
 * out of date (stale).
 */
export const StatusBanner = ({ status }: StatusBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { icon, label } = getBannerContent(status);

	return (
		<div css={[styles.banner, status === 'updated' && styles.bannerSuccess]}>
			<Inline space="space.075" alignBlock="center">
				{icon}
				{formatMessage(label)}
			</Inline>
		</div>
	);
};
